import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { flattenMenu, isSearchHotkey, listRows, matchPages, matchesQuery, topHits } from './searchIndex';
import { getVendors } from '../../Redux/Actions/Vendor';
import { getInventories } from '../../Redux/Actions/Inventory';
import { getPurchaseOrders } from '../../Redux/Actions/PurchaseOrder';
import { getPurchaseEntrys } from '../../Redux/Actions/PurchaseEntry';
import { getRoleManagement } from '../../Redux/Actions/RoleManagement';
import { getProcessItems } from '../../Redux/Actions/Processing';
import { get_customers, get_invoices, get_items } from '../../Redux/Actions/Account';

const LIMIT = 4;
const INV_TYPES = [
  [1, 'Base Paper'],
  [2, 'Film'],
  [3, 'Chemical'],
  [4, 'Ink'],
  [5, 'Packaging'],
  [6, 'Other'],
];

const row = (id, group, title, subtitle, path, query) => ({
  id, group, title, subtitle, path, query,
});

const GlobalSearch = ({ filteredTabs }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pages = useMemo(() => flattenMenu(filteredTabs), [filteredTabs]);
  const allowed = useMemo(() => new Set((filteredTabs || []).map((t) => t.path)), [filteredTabs]);

  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (!isSearchHotkey(e)) return;
      e.preventDefault();
      const el = document.getElementById('global-search-input');
      el?.focus();
      el?.select();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  useEffect(() => {
    const q = input.trim();
    const pageHits = matchPages(q, pages);
    if (q.length < 2) {
      setOptions(pageHits);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setOptions(pageHits);
    setLoading(true);
    const timer = setTimeout(async () => {
      const jobs = [];
      const body = { limit: 20, page: 1, search: q };

      if (allowed.has('/vendor-management')) {
        jobs.push(dispatch(getVendors(body)).then((res) =>
          topHits(listRows(res), q, (v) => [v.name, v.vendorCode], LIMIT)
            .map((v) => row(`vendor:${v._id}`, 'Vendors', v.name || v.vendorCode || 'Vendor', v.vendorCode || '', '/vendor-management', v.name || q))
        ));
      }
      if (allowed.has('/purchase-order')) {
        jobs.push(dispatch(getPurchaseOrders(body)).then((res) =>
          topHits(listRows(res), q, (p) => [p.poNumber, p.vendorName], LIMIT)
            .map((p) => row(`po:${p._id}`, 'Purchase Orders', p.poNumber || 'PO', p.vendorName || '', '/purchase-order', p.poNumber || q))
        ));
      }
      if (allowed.has('/purchase-entry')) {
        jobs.push(dispatch(getPurchaseEntrys(body)).then((res) =>
          topHits(listRows(res), q, (p) => [p.purchaseEntryNumber, p.poNumber, p.vendorName], LIMIT)
            .map((p) => row(`pe:${p._id}`, 'Purchase Entries', p.purchaseEntryNumber || p.poNumber || 'Entry', p.vendorName || '', '/purchase-entry', p.purchaseEntryNumber || q))
        ));
      }
      if (allowed.has('/user-role')) {
        jobs.push(dispatch(getRoleManagement(body)).then((res) =>
          topHits(listRows(res), q, (u) => [u.userName, u.email], LIMIT)
            .map((u) => row(`user:${u._id}`, 'Users', u.userName || u.email || 'User', u.role || '', '/user-role', u.userName || q))
        ));
      }
      if (allowed.has('/processing-unit-list')) {
        jobs.push(dispatch(getProcessItems(body)).then((res) =>
          topHits(listRows(res), q, (p) => [p.machineName, p?.productId?.name, p.lotNumber], LIMIT)
            .map((p) => row(`proc:${p._id}`, 'Processing', p.machineName || p?.productId?.name || 'Job', p.lotNumber || '', '/processing-unit-list', p.machineName || q))
        ));
      }
      if (allowed.has('/inventory')) {
        INV_TYPES.forEach(([inventoryType, label]) => {
          jobs.push(dispatch(getInventories({ ...body, limit: 8, inventoryType })).then((res) =>
            topHits(listRows(res), q, (i) => [i.fullItem, i.itemName], LIMIT)
              .map((i) => row(`item:${inventoryType}:${i._id}`, 'Inventory', i.fullItem || i.itemName || 'Item', label, '/inventory', i.fullItem || q))
          ));
        });
      }
      if (allowed.has('/account')) {
        // ponytail: billing lists have no search param; client-filter + slice. Add $regex+$limit if these collections grow.
        jobs.push(dispatch(get_customers({})).then((res) =>
          topHits(listRows(res), q, (c) => [c.customerName, c.customerCode], LIMIT)
            .map((c) => row(`acct-cust:${c._id}`, 'Account Customers', c.customerName || 'Customer', c.customerCode || '', `/account/customers/${c._id}`, c.customerName || q))
        ));
        jobs.push(dispatch(get_items({})).then((res) =>
          topHits(listRows(res), q, (i) => [i.type, i.group, i.gsm, i.itemCode], LIMIT)
            .map((i) => row(
              `acct-item:${i._id}`,
              'Account Items',
              [i.type, i.itemCode].filter(Boolean).join(' · ') || 'Item',
              [i.group, i.gsm && `${i.gsm} GSM`].filter(Boolean).join(' · '),
              '/account/items',
              i.itemCode || i.type || q,
            ))
        ));
        jobs.push(dispatch(get_invoices({})).then((res) =>
          topHits(listRows(res), q, (p) => [p.invoiceNo, p.customer?.customerName], LIMIT)
            .map((p) => row(`acct-inv:${p._id}`, 'Account Invoices', p.invoiceNo || 'Invoice', p.customer?.customerName || p.invoiceYear || '', '/account/invoice', p.invoiceNo || q))
        ));
      }

      const settled = await Promise.allSettled(jobs);
      if (cancelled) return;
      const records = settled.flatMap((s) => (s.status === 'fulfilled' ? s.value : []));
      setOptions([...pageHits, ...records]);
      setLoading(false);
    }, 280);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [input, pages, allowed, dispatch]);

  const go = (opt) => {
    if (!opt?.path) return;
    const to = opt.query ? `${opt.path}?q=${encodeURIComponent(opt.query)}` : opt.path;
    navigate(to);
    setInput('');
  };

  return (
    <Autocomplete
      size="small"
      fullWidth
      autoHighlight
      blurOnSelect
      value={null}
      openOnFocus
      filterOptions={(x) => x}
      options={options}
      loading={loading}
      inputValue={input}
      onInputChange={(_, v, reason) => { if (reason !== 'reset') setInput(v); }}
      onChange={(_, v) => { if (v) go(v); }}
      forcePopupIcon={false}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.title || '')}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      groupBy={(o) => o.group}
      noOptionsText={input.trim().length < 2 ? 'Type to search pages and records' : 'No matches'}
      slotProps={{
        paper: {
          sx: {
            mt: 0.75,
            borderRadius: '12px',
            boxShadow: '0 12px 32px -8px rgba(19,19,19,0.18)',
            border: '1px solid rgba(19,19,19,0.08)',
          },
        },
      }}
      renderGroup={(params) => (
        <Box key={params.key} component="li">
          <Typography sx={{ px: 1.5, pt: 1, pb: 0.25, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8A8A8A', fontFamily: 'Satoshi-Bold' }}>
            {params.group}
          </Typography>
          <Box component="ul" sx={{ p: 0, m: 0 }}>{params.children}</Box>
        </Box>
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box component="li" {...rest} key={option.id} sx={{ alignItems: 'flex-start !important', py: '8px !important' }}>
            <Box minWidth={0}>
              <Typography noWrap fontSize={13} fontFamily="Satoshi-Bold" color="#131313">{option.title}</Typography>
              {option.subtitle ? (
                <Typography noWrap fontSize={11} color="#8A8A8A">{option.subtitle}</Typography>
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search pages, vendors, POs, account…"
          inputProps={{ ...params.inputProps, id: 'global-search-input', 'aria-label': 'Global search' }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#E77843' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} sx={{ mr: 0.5, color: '#E77843' }} /> : null}
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    fontSize: 11,
                    color: '#8A8A8A',
                    border: '1px solid rgba(19,19,19,0.12)',
                    borderRadius: '6px',
                    px: 0.75,
                    py: '1px',
                    mr: 0.5,
                    fontFamily: 'Satoshi-Medium, sans-serif',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ⌘K
                </Box>
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        width: '100%',
        maxWidth: 560,
        '& .MuiOutlinedInput-root': {
          height: 40,
          borderRadius: '10px',
          backgroundColor: '#F7F7F8',
          fontSize: 13,
          fontFamily: 'Satoshi-Medium, sans-serif',
          '& fieldset': { borderColor: 'rgba(19,19,19,0.10)' },
          '&:hover fieldset': { borderColor: 'rgba(231,120,67,0.4)' },
          '&.Mui-focused fieldset': { borderColor: '#E77843', borderWidth: 1 },
        },
      }}
    />
  );
};

export default GlobalSearch;
